import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ensureMiniAppBridge, waitForAnyInitData, isAnyMiniApp, } from "@/shared/lib/miniApp";
import { getTelegramWebApp, getTelegramLaunchInitData, } from "@/shared/lib/telegram";
import { getMaxWebApp, getMaxInitData } from "@/shared/lib/max";
/**
 * Deep-link'и ботов в миниап: «поделиться песней», «релиз трека» и действия над
 * треком (продление / караоке / видеообложка).
 *
 * Расхождение платформ:
 *  - TG: WebApp-кнопка ведёт прямо на URL с путём (`/share?audio_id=<id>`,
 *    `/extend-song?audioId=<id>` и т.п.).
 *  - MAX: OpenAppButton открывает миниап по КОРНЕВОМУ URL бота (диплинков на
 *    путь у MAX нет), а payload прилетает в start-параметре. Поэтому бот шлёт
 *    `payload=share-audio_<id>` (поделиться), `payload=release_<id>` (релиз)
 *    или `payload=go_<route>_<id>` (действие над треком), а приложение на корне
 *    ловит его и само уводит на нужную страницу (см. useShareDeepLink).
 */
const SHARE_AUDIO_RE = /^share-audio[_-](\d+)$/;
/** Payload кнопки «Релиз» (согласован с ботами): `release_<audioId>`. */
const RELEASE_AUDIO_RE = /^release[_-](\d+)$/;
/**
 * Payload кнопок «действие над треком» (согласован с ботами):
 * `go_<route>_<audioId>`, где `<route>` — ключ из ACTION_ROUTES.
 */
const ACTION_DEEPLINK_RE = /^go[_-]([a-z]+)[_-](\d+)$/;
/**
 * Белый список действий бота → страница приложения. Страницы принимают трек
 * параметром `?audioId=<id>` (см. useTrackActionSource). Новое действие
 * добавляется одной строкой сюда — разбор, редирект и «мы уже на цели»
 * работают для него автоматически.
 */
const ACTION_ROUTES = {
    extend: "/extend-song",
    karaoke: "/karaoke",
    videocover: "/video-cover",
};
function isActionRoute(value) {
    return Object.prototype.hasOwnProperty.call(ACTION_ROUTES, value);
}
/**
 * Payload действия над треком (`go_<route>_<id>`) → маршрут + audio_id.
 * Неизвестный маршрут (не из белого списка) и нечисловой/неположительный id
 * игнорируются — вернём null, и диплинк отработает как обычный запуск.
 */
export function parseActionDeepLink(raw) {
    if (raw == null)
        return null;
    const m = ACTION_DEEPLINK_RE.exec(raw);
    if (!m)
        return null;
    const route = m[1];
    if (!isActionRoute(route))
        return null;
    const n = Number(m[2]);
    return Number.isFinite(n) && n > 0
        ? { pathname: ACTION_ROUTES[route], audioId: n }
        : null;
}
/**
 * Похоже ли значение на НАШ payload (любого из форматов). Нужно для
 * ключ-агностичного поиска внутри подписанной initData, где имя ключа неизвестно.
 */
function isKnownDeepLinkPayload(value) {
    return (SHARE_AUDIO_RE.test(value) ||
        RELEASE_AUDIO_RE.test(value) ||
        parseActionDeepLink(value) != null);
}
/**
 * Целевые страницы диплинков: попав на любую из них, стартовый диплинк считаем
 * доставленным (TG приходит туда напрямую, MAX — нашим редиректом).
 */
const DEEP_LINK_TARGET_PATHS = [
    "/share",
    "/release",
    ...Object.values(ACTION_ROUTES),
];
function isDeepLinkTargetPath(pathname) {
    return DEEP_LINK_TARGET_PATHS.some((target) => pathname === target || pathname.startsWith(`${target}/`));
}
/**
 * Извлечь audio_id из значения launch-параметра. Понимает и неймспейснутый
 * `share-audio_<id>` (MAX payload), и голый `<id>` (TG query). Возвращает
 * положительный id или null.
 */
export function parseShareAudioId(raw) {
    if (raw == null)
        return null;
    const m = SHARE_AUDIO_RE.exec(raw);
    const digits = m ? m[1] : raw;
    const n = Number(digits);
    return Number.isFinite(n) && n > 0 ? n : null;
}
/**
 * audio_id из payload кнопки «Релиз» (`release_<id>` / `release-<id>`). Голый
 * id сюда НЕ подходит — он означает «поделиться» (исторический формат TG).
 */
export function parseReleaseAudioId(raw) {
    if (raw == null)
        return null;
    const m = RELEASE_AUDIO_RE.exec(raw);
    if (!m)
        return null;
    const n = Number(m[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
}
/** Ключи, под которыми MAX/TG кладут значение start-параметра. */
const START_PARAM_KEYS = [
    "WebAppStartParam",
    "tgWebAppStartParam",
    "startapp",
    "start_param",
];
/** Достать start-параметр из плоского набора URL-params (query или hash). */
function pickStartParam(params) {
    for (const key of START_PARAM_KEYS) {
        const v = params.get(key);
        if (v)
            return v;
    }
    // MAX/TG кладут подписанную initData-строку в WebAppData/tgWebAppData —
    // start_param закодирован ВНУТРИ неё (как в TG initData). Распаковываем ТЕМ ЖЕ
    // разбором, что и для строки с моста (pickStartParamFromInitData): он знает
    // больше имён ключа и умеет ключ-агностичный поиск нашего payload. Раньше тут
    // был свой урезанный разбор (только `start_param`/`startapp`), из-за чего
    // payload, приехавший в URL под другим ключом, оставался недостижим.
    const packed = params.get("WebAppData") ?? params.get("tgWebAppData") ?? null;
    if (packed) {
        let decoded = packed;
        try {
            decoded = decodeURIComponent(packed);
        }
        catch {
            /* оставляем как есть */
        }
        return pickStartParamFromInitData(decoded);
    }
    return null;
}
/**
 * Достать start_param из подписанной initData-СТРОКИ моста (key=value&…).
 *
 * MAX кладёт payload из `OpenAppButton` именно в строку `initData`
 * (док maxapi: «payload… передан в initData мини-приложения»), но точное имя
 * ключа не задокументировано и может отличаться от TG (`start_param`). Поэтому:
 *  a) пробуем известные имена ключа;
 *  b) КЛЮЧ-АГНОСТИЧНО ищем любое значение НАШЕГО формата (`share-audio_<id>`,
 *     `release_<id>`, `go_<route>_<id>` — они известны точно) — так находим
 *     payload под любым именем ключа.
 */
function pickStartParamFromInitData(initData) {
    if (!initData)
        return null;
    const params = new URLSearchParams(initData);
    const known = params.get("start_param") ??
        params.get("startapp") ??
        params.get("start_app") ??
        params.get("payload");
    if (known)
        return known;
    for (const value of params.values()) {
        if (isKnownDeepLinkPayload(value))
            return value;
    }
    return null;
}
/**
 * Сырой launch-параметр текущего запуска миниаппа. Читаем независимо от
 * готовности моста, из всех каналов, которыми MAX/TG передают старт-параметр:
 *  1) URL-query (`?WebAppStartParam=`/`?startapp=`/…);
 *  2) URL-hash (`#tgWebAppStartParam=`/`#WebAppData=…` — MAX/TG кладут сюда);
 *  3) распакованная подписанная строка initData из URL (`WebAppData`/
 *     `tgWebAppData`), внутри которой сидит `start_param`;
 *  4) уже разобранный мостом `initDataUnsafe.start_param` (если мост готов);
 *  5) start_param внутри подписанной initData-СТРОКИ моста
 *     (`window.WebApp.initData` / `Telegram.WebApp.initData`).
 * Порядок: сначала URL (доступно синхронно, без моста), затем мост.
 *
 * Канал 5 критичен для MAX-нативного запуска: мост MAX (max-web-app.js)
 * подчищает launch-хэш из URL при инициализации — ещё ДО старта app-бандла,
 * поэтому каналы 1–3 к тому моменту пусты. `initDataUnsafe.start_param` (4)
 * тоже не всегда заполнен, а вот сама подписанная строка `initData` содержит
 * `start_param` (как у TG) и сохраняется на мосту — из неё и достаём.
 */
export function readShareLaunchParam() {
    if (typeof window === "undefined")
        return null;
    const fromQuery = pickStartParam(new URLSearchParams(window.location.search));
    if (fromQuery)
        return fromQuery;
    const fromHash = pickStartParam(new URLSearchParams(window.location.hash.replace(/^#/, "")));
    if (fromHash)
        return fromHash;
    const unsafe = getMaxWebApp()?.initDataUnsafe ??
        getTelegramWebApp()?.initDataUnsafe;
    const rawUnsafe = unsafe?.["start_param"] ?? unsafe?.["startapp"];
    if (rawUnsafe != null)
        return String(rawUnsafe);
    // ВАЖНО: берём initData через getMaxInitData()/getTelegramLaunchInitData(), а
    // не напрямую с моста. Обе функции знают про ранний снимок и launch-URL,
    // поэтому payload достаётся и тогда, когда мост не доехал (async-скрипты,
    // заблокированный хост на мобильной сети) — раньше в этом случае канал был
    // мёртв, хотя подписанная строка физически лежала в адресе страницы.
    const fromBridgeInitData = pickStartParamFromInitData(getMaxInitData()) ??
        pickStartParamFromInitData(getTelegramLaunchInitData());
    if (fromBridgeInitData)
        return fromBridgeInitData;
    // 6) прямое свойство моста — некоторые версии MAX кладут start-param сюда.
    const webApp = getMaxWebApp();
    const direct = webApp?.["startParam"] ?? webApp?.["start_param"];
    if (direct != null && String(direct))
        return String(direct);
    return null;
}
/**
 * Снимок launch-параметра, сделанный СРАЗУ при загрузке модуля — до первого
 * рендера React и любых редиректов/очисток URL. Критично для MAX: миниап
 * открывается на корне `/`, и пока идёт асинхронная авторизация (isLoggedIn
 * ещё false) роут `/` успевает редиректнуть гостя на `/generate-song`
 * `<Navigate>`-ом, который ТЕРЯЕТ hash (там MAX держит данные запуска). К тому
 * же эффект `<Navigate>` (child) выполняется РАНЬШЕ эффекта useShareDeepLink
 * (parent), т.е. к моменту чтения в хуке URL уже затёрт → payload «поделиться»
 * пропадал и MAX открывал главную вместо `/share`. Ранний снимок читает URL
 * (query+hash) и мост, пока они целы; launch-параметр в рамках сессии не
 * меняется, поэтому кэшируем его один раз при импорте модуля.
 */
const INITIAL_LAUNCH_PARAM = typeof window !== "undefined" ? readShareLaunchParam() : null;
/**
 * audio_id share-диплинка, известный СИНХРОННО на старте (из раннего снимка).
 * У MAX мост готов к загрузке модуля (глобальный defer-скрипт), поэтому payload
 * из initData-строки доступен сразу. MiniAppContent использует это, чтобы на `/`
 * не мигать гостевым редиректом на `/generate-song`, пока useShareDeepLink уводит
 * на `/share`. null — обычный запуск (не share-диплинк).
 */
export const INITIAL_SHARE_AUDIO_ID = parseShareAudioId(INITIAL_LAUNCH_PARAM);
/** То же для кнопки «Релиз» (`release_<id>`) — уводим на `/release`. */
const INITIAL_RELEASE_AUDIO_ID = parseReleaseAudioId(INITIAL_LAUNCH_PARAM);
/** То же для кнопок действий (`go_<route>_<id>`) — уводим на их страницу. */
const INITIAL_ACTION_DEEPLINK = parseActionDeepLink(INITIAL_LAUNCH_PARAM);
// Обработан ли уже стартовый диплинк (useShareDeepLink увёл на целевую
// страницу). Нужно, чтобы `isShareDeepLinkPending()` перестал «залипать» после
// того как пользователь позже сам навигирует на "/" в рамках сессии.
let shareDeepLinkHandled = false;
/**
 * Ждём ли мы синхронно известный стартовый диплинк, который `useShareDeepLink`
 * вот-вот уведёт на целевую страницу (`/share`, `/release`, страница действия
 * над треком). Пока да — `MiniAppContent` рендерит на `/` лоадер вместо
 * гостевого редиректа на `/generate-song`, чтобы не мигать главной перед
 * уходом на целевую страницу. После обработки (или в обычном запуске без
 * диплинка) — false.
 */
export function isShareDeepLinkPending() {
    return ((INITIAL_SHARE_AUDIO_ID != null ||
        INITIAL_RELEASE_AUDIO_ID != null ||
        INITIAL_ACTION_DEEPLINK != null) &&
        !shareDeepLinkHandled);
}
/**
 * Пометить стартовый диплинк обработанным. Вызывать, когда мы ДОСТИГЛИ цели —
 * смонтировалась целевая страница (ShareCreatePage, ReleasePage, страница
 * действия над треком). До этого pending держится, и HomeRoute обоих шеллов
 * редиректит `/` на цель, что самоисправляет любые лишние заходы на `/`
 * (напр. при mismatch-очистке в AuthProvider). После — поздняя ручная
 * навигация на "/" работает штатно.
 */
export function markShareDeepLinkConsumed() {
    shareDeepLinkHandled = true;
}
/**
 * Location-дескриптор ДЕКЛАРАТИВНОГО редиректа `/` → `/share?audio_id=<id>`
 * (или `/release?audioId=<id>`) для стартового диплинка. Используется в
 * HomeRoute обоих шеллов вместо гостевого редиректа/дашборда — детерминированно,
 * синхронно с рендером, без гонки эффектов (прежний вариант через эффект
 * useShareDeepLink терял навигацию при переключении шеллов
 * Content↔MiniAppContent). Сохраняем исходные search+hash (там подписанная
 * initData для авторизации), добавляем id трека. null — если стартового
 * диплинка нет.
 */
export function getShareRedirectLocation() {
    if (typeof window === "undefined") {
        return null;
    }
    const search = new URLSearchParams(window.location.search);
    if (INITIAL_ACTION_DEEPLINK != null) {
        search.set("audioId", String(INITIAL_ACTION_DEEPLINK.audioId));
        return {
            pathname: INITIAL_ACTION_DEEPLINK.pathname,
            search: `?${search.toString()}`,
            hash: window.location.hash,
        };
    }
    if (INITIAL_RELEASE_AUDIO_ID != null) {
        search.set("audioId", String(INITIAL_RELEASE_AUDIO_ID));
        return {
            pathname: "/release",
            search: `?${search.toString()}`,
            hash: window.location.hash,
        };
    }
    if (INITIAL_SHARE_AUDIO_ID == null) {
        return null;
    }
    search.set("audio_id", String(INITIAL_SHARE_AUDIO_ID));
    return {
        pathname: "/share",
        search: `?${search.toString()}`,
        hash: window.location.hash,
    };
}
/**
 * Безопасный дамп подписанной initData-строки для диагностики: показывает имена
 * ключей и значения, но РЕДАКТИРУЕТ `hash` (подпись) и `user` (перс.данные) —
 * их длиной. Нужен, чтобы на реальном MAX увидеть, под КАКИМ ключом лежит
 * payload `share-audio_<id>`, не сливая подпись в логи.
 */
function debugInitDataEntries(initData) {
    if (!initData)
        return null;
    const out = {};
    for (const [k, v] of new URLSearchParams(initData)) {
        out[k] = k === "hash" || k === "user" ? `[${v.length} chars]` : v;
    }
    return out;
}
/**
 * Рут-обработчик deep-link'ов ботов: если миниап открылся на корне с
 * launch-параметром `share-audio_<id>` (MAX) — уводит на `/share?audio_id=<id>`,
 * с `release_<id>` — на `/release?audioId=<id>`, с `go_<route>_<id>` — на
 * страницу действия (`/extend-song|/karaoke|/video-cover`)`?audioId=<id>`.
 * Идемпотентен: на самих целевых страницах ничего не делает (TG приходит туда
 * напрямую). В обычном браузере — no-op (нет launch-параметра / нет моста).
 */
export function useShareDeepLink() {
    const navigate = useNavigate();
    const location = useLocation();
    React.useEffect(() => {
        if (isDeepLinkTargetPath(location.pathname)) {
            // Уже на целевой странице (декларативным редиректом HomeRoute или прямой
            // TG-ссылкой) — стартовый диплинк обработан, снимаем pending, чтобы
            // поздняя навигация пользователя на "/" рендерилась штатно.
            shareDeepLinkHandled = true;
            return;
        }
        let cancelled = false;
        void (async () => {
            // Ранний снимок (сделан до затирания URL), иначе — живое чтение.
            let raw = INITIAL_LAUNCH_PARAM ?? readShareLaunchParam();
            let audioId = parseShareAudioId(raw);
            let releaseAudioId = parseReleaseAudioId(raw);
            let action = parseActionDeepLink(raw);
            // Иначе — MAX-нативный запуск: payload в мосте, который может быть не
            // готов на старте. Дожидаемся и перечитываем (в браузере — no-op).
            if (audioId == null && releaseAudioId == null && action == null) {
                await ensureMiniAppBridge();
                await waitForAnyInitData(3000, 100);
                if (cancelled)
                    return;
                raw = INITIAL_LAUNCH_PARAM ?? readShareLaunchParam();
                audioId = parseShareAudioId(raw);
                releaseAudioId = parseReleaseAudioId(raw);
                action = parseActionDeepLink(raw);
            }
            // ВРЕМЕННАЯ ДИАГНОСТИКА (можно убрать после подтверждения на MAX): в
            // мини-аппе логируем, что и откуда разобралось. `hash`/`user` в дампе
            // редактируются (debugInitDataEntries), значение payload не секретно.
            // Цель — увидеть на реальном MAX, под каким ключом лежит share-audio_<id>.
            if (isAnyMiniApp()) {
                const maxApp = getMaxWebApp();
                console.info("[shareDeepLink][mini-app-debug]", {
                    resolvedAudioId: audioId,
                    resolvedReleaseAudioId: releaseAudioId,
                    resolvedAction: action,
                    initialSnapshot: INITIAL_LAUNCH_PARAM,
                    maxInitData: debugInitDataEntries(getMaxWebApp()?.initData),
                    maxInitDataUnsafe: maxApp?.["initDataUnsafe"]
                        ? Object.keys(maxApp["initDataUnsafe"])
                        : null,
                    maxTopKeys: maxApp ? Object.keys(maxApp) : null,
                    tgInitData: debugInitDataEntries(getTelegramWebApp()?.initData),
                    // Ключевое для разбора «сессия не подтянулась» на телефоне: доехал ли
                    // мост Telegram и откуда в итоге взялась подписанная строка. Если
                    // tgBridge=false, а tgResolvedInitData не пуст — сработал URL-фолбэк
                    // (ровно та починка, ради которой всё затевалось).
                    tgBridge: Boolean(getTelegramWebApp()),
                    tgResolvedInitData: debugInitDataEntries(getTelegramLaunchInitData()),
                    maxResolvedInitData: debugInitDataEntries(getMaxInitData()),
                    href: window.location.href,
                });
            }
            // ВАЖНО: pending здесь НЕ снимаем. Снятие происходит только когда
            // ShareCreatePage реально смонтировался (markShareDeepLinkConsumed) —
            // т.е. мы ДОСТИГЛИ /share. Иначе при mismatch-очистке в AuthProvider идёт
            // лишний ре-рендер на "/", и если pending уже снят — HomeRoute мигает
            // гостевым редиректом на /generate-song (баг «работает через раз»).
            // Пока не дошли до /share, HomeRoute держит <Navigate /share> и любой
            // повторный заход на "/" снова уводит на /share.
            if (cancelled)
                return;
            // ВАЖНО: URL запуска несёт подписанную initData миниаппа
            // (`?WebAppStartParam=…`, `#WebAppData=…`) — из неё авторизуется MAX/TG
            // (см. getMaxInitData: мост, иначе fallback на URL). Эффекты детей в
            // React выполняются РАНЬШЕ родительских, т.е. этот редирект случается до
            // авторизации в AuthProvider. Поэтому переносим query и hash как есть:
            // затерев их, мы бы оставили приложение без initData → 401 и разлогин.
            const search = new URLSearchParams(window.location.search);
            // Кнопка действия над треком из бота (продление/караоке/видеообложка):
            // страницы действий ждут `audioId` в query — ровно как `/release`.
            if (action != null) {
                search.set("audioId", String(action.audioId));
                navigate({
                    pathname: action.pathname,
                    search: `?${search.toString()}`,
                    hash: window.location.hash,
                }, { replace: true });
                return;
            }
            // Кнопка «Релиз» из бота: страница формы ждёт `audioId` в query.
            if (releaseAudioId != null) {
                search.set("audioId", String(releaseAudioId));
                navigate({
                    pathname: "/release",
                    search: `?${search.toString()}`,
                    hash: window.location.hash,
                }, { replace: true });
                return;
            }
            if (audioId == null)
                return;
            search.set("audio_id", String(audioId));
            navigate({
                pathname: "/share",
                search: `?${search.toString()}`,
                hash: window.location.hash,
            }, { replace: true });
        })();
        return () => {
            cancelled = true;
        };
        // Один раз на маунт приложения — launch-параметр не меняется в рамках сессии.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
